import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../supabaseClient'; // Import Supabase client
import { getDeviceToken, setDeviceToken, getDeviceName, normalizePhone } from '../../lib/deviceTrust';
import SMSVerification from './SMSVerification';

const LoginForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSMS, setShowSMS] = useState(false);
  const [smsPhone, setSmsPhone] = useState('');
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [pendingOrgId, setPendingOrgId] = useState<string | null>(null);

  const handleSMSVerified = async (deviceToken: string) => {
    try {
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

      await supabase.from('trusted_devices').insert({
        user_id: pendingUserId,
        device_token: deviceToken,
        device_name: getDeviceName(),
        organization_id: pendingOrgId,
        expires_at: thirtyDaysFromNow.toISOString(),
      });

      setDeviceToken(deviceToken);
    } catch (err) {
      console.error('Failed to save trusted device:', err);
      setDeviceToken(deviceToken);
    } finally {
      sessionStorage.removeItem('mfa_pending');
      setShowSMS(false);
    }
  };

  const handleSMSCancel = async () => {
    sessionStorage.removeItem('mfa_pending');
    setShowSMS(false);
    await supabase.auth.signOut();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("LoginForm: handleSubmit triggered."); // Log 1: Start of handler
    setLoading(true);
    setError(null);

    try {
      console.log("LoginForm: Calling supabase.auth.signInWithPassword..."); // Log 2: Before Supabase call
      const { data: signInData, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      console.log("LoginForm: supabase.auth.signInWithPassword call completed."); // Log 3: After Supabase call

      if (error) {
        console.error("LoginForm: Supabase signIn error:", error); // Log error specifically
        setError('Invalid email or password. Please try again.');
      } else {
        console.log('LoginForm: Supabase signIn successful!'); // Log success specifically

        // --- MFA Device Trust Check (fail-open) ---
        // TEMPORARY: MFA on hold - skip MFA verification until re-enabled
        // Remove this block to re-enable MFA on login
        if (true) {
          setLoading(false);
          return;
        }
        try {
          const userId = signInData.user?.id;
          if (!userId) throw new Error('No user ID after sign-in');

          const { data: profileData } = await supabase
            .from('profiles')
            .select('phone, organization_id')
            .eq('id', userId)
            .single();

          const normalizedPhone = profileData?.phone ? normalizePhone(profileData!.phone) : null;
          if (!normalizedPhone) {
            // No phone or invalid format — skip MFA, flag for banner
            sessionStorage.setItem('mfa_phone_banner', 'true');
          } else {
            const existingToken = getDeviceToken();
            let isTrusted = false;

            if (existingToken) {
              const { data: device } = await supabase
                .from('trusted_devices')
                .select('id')
                .eq('device_token', existingToken)
                .eq('user_id', userId)
                .gt('expires_at', new Date().toISOString())
                .maybeSingle();

              if (device) {
                isTrusted = true;
                await supabase
                  .from('trusted_devices')
                  .update({ last_used_at: new Date().toISOString() })
                  .eq('id', device!.id);
              }
            }

            if (!isTrusted) {
              await supabase.functions.invoke('verify-phone-send', {
                body: { phone: normalizedPhone },
              });

              // Store for MainLayout fallback if redirect fires before SMS completes
              sessionStorage.setItem('mfa_pending', JSON.stringify({
                phone: normalizedPhone,
                userId,
                orgId: profileData?.organization_id || '',
              }));

              setSmsPhone(normalizedPhone!);
              setPendingUserId(userId!);
              setPendingOrgId(profileData?.organization_id || '');
              setShowSMS(true);
              return; // Prevent setLoading(false) from firing while SMS screen is shown
            }
          }
        } catch (mfaErr) {
          setLoading(false);
          console.error('MFA check failed, proceeding without MFA:', mfaErr);
          // Fail-open: auth state listener handles navigation normally
        }
      }
    } catch (error: any) {
      console.error('LoginForm: Catch block error:', error); // Log catch block errors
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (showSMS) {
    return (
      <SMSVerification
        phone={smsPhone}
        onVerified={handleSMSVerified}
        onCancel={handleSMSCancel}
      />
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="p-3 text-sm text-status-danger bg-status-danger-light border border-status-danger/20 rounded-lg">
          {error}
        </div>
      )}
      {/* Item 5: Labels with mb-1.5 spacing */}
      <div>
        <label
          htmlFor="email"
          className="block text-sm font-medium text-text-body mb-1.5"
        >
          Email address
        </label>
        {/* Item 6: Input focus ring + font + transition */}
        <input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="block w-full px-4 py-3 font-sans text-base placeholder-text-muted border border-surface-border rounded-lg shadow-sm appearance-none focus:outline-none focus:ring-2 focus:ring-accent-blue-wash focus:border-accent-blue transition-colors"
          placeholder="you@example.com"
          disabled={loading}
        />
      </div>

      <div>
        <label
          htmlFor="password"
          className="block text-sm font-medium text-text-body mb-1.5"
        >
          Password
        </label>
        <input
          id="password"
          name="password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="block w-full px-4 py-3 font-sans text-base placeholder-text-muted border border-surface-border rounded-lg shadow-sm appearance-none focus:outline-none focus:ring-2 focus:ring-accent-blue-wash focus:border-accent-blue transition-colors"
          placeholder="Password"
          disabled={loading}
        />
      </div>

      {/* Item 9: Checkbox with 44px touch target */}
      <div className="flex items-center justify-between">
        <label htmlFor="remember-me" className="flex items-center min-h-[44px] cursor-pointer">
          <input id="remember-me" name="remember-me" type="checkbox" className="w-5 h-5 text-accent-blue border-surface-border rounded focus:ring-accent-blue" />
          <span className="ml-2 text-sm text-text-primary">Remember me</span>
        </label>
        <div className="text-sm">
          {/* Item 8: Link hover underline */}
          <Link to="/forgot-password" className="font-medium text-accent-blue hover:text-accent-blue-hover hover:underline transition-colors">Forgot your password?</Link>
        </div>
      </div>

      {/* Item 7: Button with hover lift + glow */}
      <div>
        <button
          type="submit"
          disabled={loading}
          className="flex items-center justify-center w-full px-4 py-3 min-h-[44px] text-base font-semibold text-white bg-accent-blue border border-transparent rounded-lg shadow-sm hover:bg-accent-blue-hover hover:-translate-y-0.5 hover:shadow-lg hover:shadow-blue-500/25 active:translate-y-0 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-blue disabled:opacity-50 transition-all duration-200"
        >
          {loading ? 'Signing in...' : 'Sign In'}
        </button>
      </div>
    </form>
  );
};

export default LoginForm;
